import { useNetworkStatus } from '@/composables/useNetworkStatus';
import i18n from '@/i18n';
import { useAuthStore } from '@/stores/auth';
import { useToastStore } from '@/stores/toast';
import { apiErrorMessage, MAINTENANCE_STATUS, RATE_LIMIT_STATUS } from '@/utils/apiError';
import { log } from '@/utils/log.ts';
import { syncFormLabel } from '@/utils/syncFormLabel';
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';

export type QueueItemStatus = 'pending' | 'syncing' | 'success' | 'error' | 'conflict';

export interface QueueItem {
    id: string;
    idempotencyKey: string;
    endpoint: string;
    method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    data: any;
    headers?: Record<string, string>;
    timestamp: number;
    retries: number;
    maxRetries: number;
    status: QueueItemStatus;
    error?: string;
    metadata?: {
        formName?: string;
        description?: string;
        userId?: number;
    };
    onSuccess?: (response: AxiosResponse) => Promise<void> | void;
}

export const useSyncQueueStore = defineStore('syncQueue', () => {
    const queue = ref<QueueItem[]>([]);
    const isSyncing = ref(false);
    const toast = useToastStore();
    const { isOnline } = useNetworkStatus();

    // Get translation function from i18n instance
    const t = i18n.global.t;

    // Computed
    const pendingItems = computed(() => queue.value.filter((item) => item.status === 'pending'));
    const failedItems = computed(() => queue.value.filter((item) => item.status === 'error'));
    const hasPendingItems = computed(() => pendingItems.value.length > 0);
    const hasFailedItems = computed(() => failedItems.value.length > 0);

    // Load queue from localStorage on init
    const loadQueue = () => {
        try {
            const stored = localStorage.getItem('sync-queue');
            if (stored) {
                const parsed = JSON.parse(stored);
                if (!Array.isArray(parsed)) {
                    queue.value = [];
                    persistQueue();
                    return;
                }

                queue.value = parsed
                    .filter((item: Partial<QueueItem>) => item.id && item.endpoint && item.method)
                    .map((item: QueueItem) => ({
                        ...item,
                        idempotencyKey: item.idempotencyKey || item.id,
                        headers: item.headers || {},
                        retries: item.retries || 0,
                        maxRetries: item.maxRetries || 3,
                        status: item.status === 'syncing' ? 'pending' : item.status || 'pending',
                    }));
            }
        } catch (error) {
            log('Failed to load sync queue:', error);
            queue.value = [];
        }
    };

    // Persist queue to localStorage
    const persistQueue = (): boolean => {
        try {
            localStorage.setItem('sync-queue', JSON.stringify(queue.value));
            return true;
        } catch (error) {
            log('Failed to persist sync queue:', error);
            toast.error(t('sync.persistFailed'), 0);
            return false;
        }
    };

    // Generate unique ID
    const generateId = (): string => {
        if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
            return crypto.randomUUID();
        }

        return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    };

    const cloneQueueData = (data: any) => {
        if (!data) return data;
        return JSON.parse(JSON.stringify(data));
    };

    // Add item to queue
    const addToQueue = (
        endpoint: string,
        method: QueueItem['method'],
        data: any,
        metadata?: QueueItem['metadata'],
        onSuccess?: (response: AxiosResponse) => Promise<void> | void,
        options: {
            headers?: Record<string, string>;
            idempotencyKey?: string;
            maxRetries?: number;
        } = {},
    ): string => {
        const id = generateId();
        const idempotencyKey = options.idempotencyKey || id;

        // Deep clone data to convert reactive proxies to plain objects
        // This ensures values are properly preserved when serialized to localStorage
        const clonedData = cloneQueueData(data);

        const item: QueueItem = {
            id,
            idempotencyKey,
            endpoint,
            method,
            data: clonedData,
            headers: {
                ...options.headers,
                'X-Idempotency-Key': idempotencyKey,
            },
            timestamp: Date.now(),
            retries: 0,
            maxRetries: options.maxRetries || 3,
            status: 'pending',
            metadata,
            onSuccess,
        };

        queue.value.push(item);
        if (!persistQueue()) {
            queue.value = queue.value.filter((queuedItem) => queuedItem.id !== id);
            throw new Error(t('sync.persistFailed'));
        }

        log(`Added item to queue: ${method} ${endpoint}`, item);
        toast.info(t('sync.formSaved'), 5000);

        // Notify service worker about new queue item
        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
            navigator.serviceWorker.controller.postMessage({
                type: 'QUEUE_ITEM_ADDED',
                itemId: id,
            });
        }

        // Try to sync immediately if online
        if (isOnline.value) {
            log('Online detected, attempting immediate sync...');
            setTimeout(() => processQueue(), 1000);
        } else {
            log('Offline detected, item queued for later sync');
        }

        return id;
    };

    // Remove item from queue
    const removeFromQueue = (id: string) => {
        queue.value = queue.value.filter((item) => item.id !== id);
        persistQueue();
    };

    // Update item status
    const updateItemStatus = (id: string, status: QueueItemStatus, error?: string) => {
        const item = queue.value.find((i) => i.id === id);
        if (item) {
            item.status = status;
            item.error = error;
            persistQueue();
        }
    };

    // Process single queue item. 'throttled' means the API refused it with a 429,
    // which it will do for every request after it as well until the limit resets.
    const processQueueItem = async (item: QueueItem, apiInstance: any): Promise<'synced' | 'failed' | 'throttled'> => {
        try {
            log(`Processing item ${item.id}: ${item.method} ${item.endpoint}`);
            updateItemStatus(item.id, 'syncing');

            const config: AxiosRequestConfig = {
                method: item.method,
                url: item.endpoint,
                data: item.data,
                // The queue handles auth failures itself (pauses the item, see below), so
                // a 401 here must NOT trip the global interceptor's signOut/redirect.
                skipAuthRedirect: true,
                // Likewise a 429 only keeps the item pending; the rate-limit notice is
                // for what the user just did, not for a background replay.
                skipRateLimitNotice: true,
                headers: {
                    ...item.headers,
                    'X-Idempotency-Key': item.idempotencyKey,
                },
            } as AxiosRequestConfig;

            log(`Making API request:`, config);
            const response: AxiosResponse = await apiInstance.request(config);

            if (response.status >= 200 && response.status < 300) {
                log(`Item ${item.id} processed successfully`);
                updateItemStatus(item.id, 'success');

                // Execute callback if provided
                if (item.onSuccess) {
                    try {
                        log(`Executing onSuccess callback for item ${item.id}`);
                        await item.onSuccess(response);
                        log(`onSuccess callback completed for item ${item.id}`);
                    } catch (callbackError) {
                        log(`onSuccess callback failed for item ${item.id}:`, callbackError);
                        // Don't fail the entire sync if callback fails
                    }
                }

                toast.success(t('sync.formSubmitted', { formName: syncFormLabel(item.metadata?.formName, t) }));

                // Remove successful items after a delay
                setTimeout(() => removeFromQueue(item.id), 5000);
                return 'synced';
            }

            return 'failed';
        } catch (error: any) {
            const axiosError = error as AxiosError;
            const status = axiosError.response?.status;
            const errorMessage = apiErrorMessage(error, t, axiosError.message || t('sync.processingError'));

            // Handle different error types
            if (status === 409) {
                // Conflict (e.g. the resource changed since the offline edit). Terminal:
                // a plain retry replays the same stale data and re-fails, so the user must
                // resolve it explicitly (overwriteItem) — see SyncQueueIndicator.
                updateItemStatus(item.id, 'conflict', errorMessage);
                toast.warning(t('sync.conflictDetected'));
            } else if (status === 422) {
                // Validation error - don't retry
                updateItemStatus(item.id, 'error', errorMessage);
                toast.error(t('sync.validationError'));
            } else if (status === 401 || status === 403) {
                // Auth failure (typically an expired session) is transient w.r.t. the
                // item — the mutation itself is valid, only the session lapsed. Keep it
                // pending (like 425) so it flushes after re-auth instead of surfacing as
                // a permanent "failed" item. The auth guard in processQueue() stops it
                // from spinning while signed out.
                updateItemStatus(item.id, 'pending', errorMessage);
            } else if (status === 425) {
                // The server is still processing the original request for this idempotency
                // key. Transient — keep pending and retry later without burning maxRetries.
                updateItemStatus(item.id, 'pending', errorMessage);
            } else if (status === MAINTENANCE_STATUS) {
                // The API is in maintenance mode (a deploy). Like 425 this says nothing
                // about the item itself, so it stays pending without burning maxRetries —
                // otherwise a deploy of a few minutes can fail a valid offline mutation.
                updateItemStatus(item.id, 'pending', errorMessage);
            } else if (status === RATE_LIMIT_STATUS) {
                // Too many requests in this minute (milo-api throttles every user). Like
                // 425 and 503 that says nothing about the item, so it stays pending without
                // burning maxRetries: a long queue alone runs into the limit.
                updateItemStatus(item.id, 'pending', errorMessage);
                return 'throttled';
            } else if (++item.retries >= item.maxRetries) {
                // Max retries reached
                updateItemStatus(item.id, 'error', errorMessage);
                toast.error(
                    t('sync.syncFailed', {
                        formName: syncFormLabel(item.metadata?.formName, t),
                        retries: item.maxRetries,
                    }),
                );
            } else {
                // Retry later (transient 5xx / network). Keep the same idempotency key so a
                // request that actually succeeded server-side is deduped, not duplicated.
                updateItemStatus(item.id, 'pending', errorMessage);
            }

            return 'failed';
        }
    };

    // Process entire queue
    const processQueue = async (apiInstance?: any) => {
        log(
            `Processing queue: online=${isOnline.value}, syncing=${isSyncing.value}, pending=${pendingItems.value.length}`,
        );

        // Never replay mutations while signed out. The queue persists in localStorage
        // and can be triggered by the service worker (background sync after a deploy),
        // the online watcher, or the widget — none of which know about auth. Without
        // this guard a logged-out/expired session replays creates as 401s, flips items
        // to a misleading failed state, and churns redundant signOuts. See SyncQueueIndicator.
        if (!useAuthStore().isAuthenticated) {
            log('Sync queue processing skipped: not authenticated');
            return;
        }

        if (!isOnline.value) {
            log('Sync queue processing skipped: offline');
            return;
        }

        if (isSyncing.value) {
            log('Sync queue processing skipped: already syncing');
            return;
        }

        if (pendingItems.value.length === 0) {
            log('Sync queue processing skipped: no pending items');
            return;
        }

        log(`Starting sync queue processing with ${pendingItems.value.length} items...`);
        isSyncing.value = true;

        // Import api dynamically to avoid circular dependency
        if (!apiInstance) {
            try {
                const { default: api } = await import('@/services/api');
                apiInstance = api;
                log('API instance imported successfully');
            } catch (error) {
                log('Failed to import API instance:', error);
                isSyncing.value = false;
                return;
            }
        }

        let processedCount = 0;
        let successCount = 0;

        try {
            // Process items sequentially to maintain order
            for (const item of pendingItems.value) {
                log(`Processing queue item ${item.id}: ${item.method} ${item.endpoint}`);
                const result = await processQueueItem(item, apiInstance);
                processedCount++;
                if (result === 'synced') successCount++;

                // The rest would be refused as well; they stay pending for the next run.
                if (result === 'throttled') {
                    log('Sync queue paused: rate limited by the API');
                    break;
                }

                // Small delay between requests to avoid overwhelming the server
                await new Promise((resolve) => setTimeout(resolve, 500));
            }

            log(`Queue processing completed: ${successCount}/${processedCount} successful`);

            if (queue.value.length > 0 && pendingItems.value.length === 0 && failedItems.value.length === 0) {
                return;
            }

            if (pendingItems.value.length === 0 && failedItems.value.length === 0) {
                toast.success(t('sync.allSynced'));
            }
        } catch (error) {
            log('Error processing sync queue:', error);
            toast.error(t('sync.processingError'));
        } finally {
            isSyncing.value = false;
            log('Queue processing finished');
        }
    };

    // Retry failed item. Keeps the same idempotency key on purpose: a transient
    // 5xx/network failure may have succeeded server-side, and the stable key lets
    // the backend dedupe instead of creating a duplicate.
    const retryItem = async (id: string) => {
        const item = queue.value.find((i) => i.id === id);
        if (item) {
            item.retries = 0;
            item.status = 'pending';
            persistQueue();
            await processQueue();
        }
    };

    // Resolve a conflict by overwriting the server's newer version with the queued
    // edit. Safe to rotate the idempotency key (the server rejected the mutation, so
    // nothing was applied) and we drop the now-stale X-Resource-Updated-At so the
    // optimistic-lock guard no longer rejects the replay.
    const overwriteItem = async (id: string) => {
        const item = queue.value.find((i) => i.id === id);
        if (!item) return;

        const newKey = generateId();
        const headers = { ...(item.headers ?? {}) };
        delete headers['X-Resource-Updated-At'];
        headers['X-Idempotency-Key'] = newKey;

        item.idempotencyKey = newKey;
        item.headers = headers;
        item.retries = 0;
        item.error = undefined;
        item.status = 'pending';
        persistQueue();
        await processQueue();
    };

    // Clear all successful items
    const clearSuccessful = () => {
        queue.value = queue.value.filter((item) => item.status !== 'success');
        persistQueue();
    };

    // Clear all items
    const clearAll = () => {
        queue.value = [];
        persistQueue();
    };

    // Watch for network status changes
    watch(isOnline, (online) => {
        log(`Network status changed: online=${online}`);
        if (online && hasPendingItems.value) {
            log('Connection restored, attempting to process queue...');
            toast.info(t('sync.connectionRestored'));
            setTimeout(() => processQueue(), 2000);
        }
    });

    // Reconcile in-memory state with a change made by another tab. Unlike loadQueue
    // this preserves the onSuccess closures of items this tab owns (functions cannot
    // cross tabs / survive JSON), and it never clobbers an in-flight sync.
    const mergeFromStorage = () => {
        if (isSyncing.value) return; // don't reset items we're actively sending

        try {
            const stored = localStorage.getItem('sync-queue');
            const parsed = stored ? JSON.parse(stored) : [];
            if (!Array.isArray(parsed)) return;

            const callbacks = new Map(queue.value.map((item) => [item.id, item.onSuccess]));

            queue.value = parsed
                .filter((item: Partial<QueueItem>) => item.id && item.endpoint && item.method)
                .map((item: QueueItem) => ({
                    ...item,
                    idempotencyKey: item.idempotencyKey || item.id,
                    headers: item.headers || {},
                    retries: item.retries || 0,
                    maxRetries: item.maxRetries || 3,
                    status: item.status === 'syncing' ? 'pending' : item.status || 'pending',
                    onSuccess: callbacks.get(item.id),
                }));
        } catch (error) {
            log('Failed to merge sync queue from storage:', error);
        }
    };

    // Initialize
    loadQueue();

    if (typeof window !== 'undefined') {
        window.addEventListener('storage', (event) => {
            if (event.key === 'sync-queue') {
                mergeFromStorage();
            }
        });
    }

    return {
        queue,
        isSyncing,
        pendingItems,
        failedItems,
        hasPendingItems,
        hasFailedItems,
        addToQueue,
        removeFromQueue,
        processQueue,
        retryItem,
        overwriteItem,
        clearSuccessful,
        clearAll,
    };
});
